<template>
  <CardLayout title="Incident Information" :loading="isLoading">
    <DialogLayout
      v-if="isOpen"
      v-model="isOpen"
      title="Viewing Video Clip"
      width="900px"
      @close="closeDialog"
    >
      <v-progress-circular
        v-if="isLoadingVideo"
        indeterminate
        color="primary"
        class="loader"
      ></v-progress-circular>
      <PopupVideoView :video="video" />
    </DialogLayout>

    <!-- Add EventVideoDialog -->
    <EventVideoDialog
      v-model="showEventDialog"
      :thumbnail="incidentAsThumbnail"
      @closed="handleEventDialogClosed"
    />

    <dl
      v-if="incident"
      class="property-list my-4"
      :class="{
        'd-flex flex-column': $vuetify.breakpoint.smAndDown,
        'two-columns': !$vuetify.breakpoint.smAndDown,
      }"
    >
      <dt>Incident ID</dt>
      <dd>{{ incident.shortId }}</dd>
      <dt>Facility</dt>
      <dd>{{ incident.facilityName }}</dd>
      <dt>Incident Type</dt>
      <dd>
        <IconIncidentType :incident="incident" />
        {{ incident.typeName }}
      </dd>
      <dt>Smart Unit/Zone</dt>
      <dd>
        <template
          v-if="
            incident.type === 'facility_motion' &&
            incident.devices &&
            incident.devices.length
          "
        >
          {{ facilityMotionUnitNames || incident.organization.name }}
        </template>
        <template v-else>{{ incident.organization.name }}</template>
      </dd>
      <dt>Priority</dt>
      <dd>
        <SeverityChip :severity="titleCase(incident.priority)" />
      </dd>
      <dt>Renter Name</dt>
      <dd>
        <UserIcon
          v-if="incident.hasDisabledLienResponder"
          left
          :responder="incident.responder"
        />
        {{ incident.responder.name }}
      </dd>
      <dt>Incident Status</dt>
      <dd>
        <IconIncidentStatus small :incident="incident" />
        {{ incident.statusName }}
      </dd>
      <dt>Incident Disposition</dt>
      <dd>{{ incident.incidentDisposition }}</dd>
      <template v-if="isVideoCameraEnabled">
        <dt>Duration</dt>
        <dd class="d-flex align-center flex-wrap" style="gap: 4px">
          {{ incident.duration }}
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-btn
                  class="ml-2"
                  depressed
                  :disabled="!isVideoAvailable(incident.incidentStartedAt)"
                  :color="
                    isVideoAvailable(incident.startedAt)
                      ? 'transparent'
                      : 'white'
                  "
                  title="View Video"
                  @click="openVideoDialog()"
                >
                  <v-icon
                    :color="
                      isVideoAvailable(incident.incidentStartedAt)
                        ? 'error'
                        : 'grey'
                    "
                    left
                  >
                    fas fa-play-circle
                  </v-icon>
                  View Video
                </v-btn>
              </span>
            </template>
            <span v-if="!isVideoAvailable(incident.incidentStartedAt)">
              Video not available. Videos are only retained for
              {{ eenVideoRetentionDays }} days.
            </span>
            <span v-if="isVideoAvailable(incident.incidentStartedAt)">
              Click here to view the video.
            </span>
          </v-tooltip>

          <!-- Generate a shareable zone-video link (for a camera-enabled zone incident). -->
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                class="ml-1"
                depressed
                :loading="generatingVideoLink"
                v-on="on"
                @click="generateVideoLink()"
              >
                <v-icon color="primary" left>mdi-link-variant</v-icon>
                Generate Link
              </v-btn>
            </template>
            <span>Share zone full video to tenant.</span>
          </v-tooltip>
        </dd>
      </template>

      <!-- Link sent to the tenant via SMS — shown whenever it exists, even if
           THIS incident's org (e.g. a unit) has no camera device of its own. -->
      <template v-if="tenantVideoUrl">
        <dt>Tenant Video Link</dt>
        <dd>
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                depressed
                v-on="on"
                @click="shareTenantVideoLink()"
              >
                <v-icon color="primary" left>mdi-eye-outline</v-icon>
                View Link
              </v-btn>
            </template>
            <span>Link sent to tenant via SMS</span>
          </v-tooltip>
        </dd>
      </template>

      <dt>Incident Started On</dt>
      <dd>{{ incident.startedAt }}</dd>

      <dt>Incident Due Date</dt>
      <dd>{{ incident.dueAt }}</dd>
    </dl>

    <!-- Shared link snackbar (used by both Generate Link and View Link). -->
    <v-snackbar
      v-model="showVideoLinkSnackbar"
      timeout="-1"
      bottom
      multi-line
      color="white"
      light
      elevation="4"
      style="z-index: 9999"
    >
      <div class="d-flex align-center mb-1">
        <v-icon color="primary" small class="mr-1">mdi-link-variant</v-icon>
        <span class="caption font-weight-bold">Tenant Link (valid 24 hrs)</span>
        <v-spacer />
        <v-btn icon x-small @click="showVideoLinkSnackbar = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
      <div
        class="caption grey--text text-truncate mb-2"
        style="max-width: 340px"
      >
        {{ generatedVideoLink }}
      </div>
      <v-btn
        small
        depressed
        color="primary"
        class="mr-2"
        @click="copyVideoLink"
      >
        <v-icon small left>{{
          linkCopied ? "mdi-check" : "mdi-content-copy"
        }}</v-icon>
        {{ linkCopied ? "Copied!" : "Copy Link" }}
      </v-btn>
      <v-btn small depressed @click="openVideoLink">
        <v-icon small left>mdi-open-in-new</v-icon>
        Open
      </v-btn>
    </v-snackbar>

    <template
      v-if="!isLoading && incident && (!incident.needsSurvey || hasTasks)"
      #actions
    >
      <v-btn color="grey lighten-2" @click="$router.go(-1)">Done</v-btn>
    </template>
  </CardLayout>
</template>

<script>
import CardLayout from "@layout/CardLayout.vue";
import DialogLayout from "@components/smart_zones/DialogLayout.vue";
import IconIncidentStatus from "@atoms/IconIncidentStatus.vue";
import IconIncidentType from "@atoms/IconIncidentType.vue";
import UserIcon from "../renters/UserIcon.vue";
import PopupVideoView from "@components/smart_zones/PopupVideoView.vue";
import { ref, computed } from "vue";
import useIncident from "./useIncident";
import useAlerts from "@tod-ui/composables/useAlerts";
import {
  EEN_VIDEO_FEED,
  EEN_CAMERA_SETTINGS,
  GENERATE_ZONE_VIDEO_TOKEN,
} from "../smart_zones/graphql";
import { titleCase } from "@tod-ui/helpers/strings";
import SeverityChip from "@tod-ui/components/SeverityChip.vue";
import EventVideoDialog from "@components/smart_zones/EventVideoDialog.vue";

export default {
  name: "CardIncidentInfo",
  components: {
    CardLayout,
    DialogLayout,
    IconIncidentStatus,
    IconIncidentType,
    UserIcon,
    PopupVideoView,
    SeverityChip,
    EventVideoDialog,
  },
  props: {
    singleColumn: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const {
      incident,
      loading: incidentLoading,
      facilityMotionUnitNames,
    } = useIncident();
    const hasTasks = computed(() => (incident.value?.tasks?.length ?? 0) > 0);
    const { addAlert } = useAlerts();
    const isOpen = ref(false);
    const showEventDialog = ref(false);
    const video = ref(null);
    const eenVideoRetentionDays = ref(0);
    const isVideoAvailable = (startedAt) => {
      if (!eenVideoRetentionDays.value) return false;
      const now = new Date();
      const startDate = new Date(startedAt);
      const diffInDays = (now - startDate) / (1000 * 60 * 60 * 24);
      return diffInDays <= eenVideoRetentionDays.value;
    };
    const isLoadingVideo = ref(false);
    const currentVideoParams = ref({
      deviceId: null,
      fromTimeStamp: null,
      organizationId: null,
    });

    const isVideoCameraEnabled = computed(() => {
      if (!incident.value?.organization?.devices) return null;
      return incident.value.organization.devices.find(
        (device) => device.type === "een" && device.subtype === "video_camera"
      );
    });

    const isLoading = computed(
      () => incidentLoading.value || isLoadingVideo.value
    );

    const incidentAsThumbnail = computed(() => {
      if (!incident.value || !isVideoCameraEnabled.value) return null;

      return {
        actorId: isVideoCameraEnabled.value.externalId,
        startTimestamp:
          incident.value.incidentStartedAt || incident.value.startedAt,
        endedAt:
          incident.value.incidentClosedAt ||
          incident.value.closedAt ||
          incident.value.closedAt,
        organizationId: incident.value.organization.ancestors[0].id,
        organization: incident.value.organization,
        incidentType: incident.value.typeName,
        deviceLocation: incident.value?.devices?.[0]?.location,
        zoneName: incident.value.organization.name,
        priority: incident.value.priority,
        events: incident.value.events || [],
      };
    });

    const generatingVideoLink = ref(false);
    const generatedVideoLink = ref("");
    const showVideoLinkSnackbar = ref(false);
    const linkCopied = ref(false);

    // The zone-video link that was sent to the tenant (persisted on the incident).
    const tenantVideoUrl = computed(
      () => incident.value?.video?.tenantUrl || ""
    );

    return {
      incident,
      hasTasks,
      isVideoCameraEnabled,
      isLoading,
      isLoadingVideo,
      isOpen,
      showEventDialog,
      video,
      currentVideoParams,
      addAlert,
      eenVideoRetentionDays,
      isVideoAvailable,
      incidentAsThumbnail,
      tenantVideoUrl,
      generatingVideoLink,
      generatedVideoLink,
      showVideoLinkSnackbar,
      linkCopied,
      facilityMotionUnitNames,
    };
  },

  apollo: {
    videoFeed: {
      query: EEN_VIDEO_FEED,
      skip() {
        return !this.currentVideoParams.deviceId;
      },
      variables() {
        return this.currentVideoParams;
      },
      update: (data) => data?.eenVideoFeed?.video ?? "",
      result({ data }) {
        if (data?.eenVideoFeed?.video) {
          this.video = data.eenVideoFeed.video;
          this.isLoadingVideo = false;
        }
      },
      error(error) {
        console.error("Error fetching video:", error);
        this.addAlert({
          type: "error",
          message: "There was an error fetching the video. Please try again.",
        });
        this.isLoadingVideo = false;
      },
    },
    eenCameraSettings: {
      query: EEN_CAMERA_SETTINGS,
      skip() {
        return (
          !this.isVideoCameraEnabled?.externalId ||
          !this.incident?.organization?.ancestors?.[0]?.id
        );
      },
      variables() {
        return {
          cameraId: this.isVideoCameraEnabled?.externalId,
          organizationId: this.incident?.organization?.ancestors?.[0]?.id,
        };
      },
      result({ data }) {
        if (data?.eenCameraSettings?.cloudRetentionDays) {
          this.eenVideoRetentionDays =
            data.eenCameraSettings.cloudRetentionDays;
        }
      },
      error(error) {
        console.error("Error fetching eenCameraSettings:", error);
        this.addAlert({
          type: "error",
          message:
            "There was an error fetching the Video Camera Settings. Please try again.",
        });
      },
    },
  },
  methods: {
    formatTimestamp(timestamp) {
      return new Date(timestamp).toISOString().replace("Z", "+00:00");
    },

    openVideoDialog() {
      this.showEventDialog = true;
    },
    fetchVideo(deviceId, fromTimeStamp, organizationId) {
      this.video = null;
      this.isOpen = true;
      this.isLoadingVideo = true;
      this.currentVideoParams = {
        deviceId,
        fromTimeStamp: this.formatTimestamp(fromTimeStamp),
        organizationId,
      };
    },

    closeDialog() {
      this.isOpen = false;
      this.video = null;
      this.currentVideoParams = {
        deviceId: null,
        fromTimeStamp: null,
        organizationId: null,
      };
    },

    handleEventDialogClosed() {
      this.showEventDialog = false;
    },

    async generateVideoLink() {
      const incidentId = this.incident?.id;
      const zoneId = this.incident?.organization?.id;
      if (!incidentId || !zoneId) return;

      this.generatingVideoLink = true;
      try {
        const { data } = await this.$apollo.mutate({
          mutation: GENERATE_ZONE_VIDEO_TOKEN,
          variables: { incidentId, zoneId },
        });
        const token = data?.generateZoneVideoToken?.token;
        if (token) {
          const base = window.location.origin;
          this.generatedVideoLink = `${base}/zone-video?token=${token}`;
          this.showVideoLinkSnackbar = true;
          this.linkCopied = false;
        } else {
          this.addAlert({
            type: "error",
            message: "Failed to generate video link.",
          });
        }
      } catch (e) {
        console.error("generateVideoLink:", e);
        this.addAlert({
          type: "error",
          message: "Error generating video link.",
        });
      } finally {
        this.generatingVideoLink = false;
      }
    },

    shareTenantVideoLink() {
      // Surface the link already sent to the tenant (copy / open).
      this.generatedVideoLink = this.tenantVideoUrl;
      this.linkCopied = false;
      this.showVideoLinkSnackbar = true;
    },

    copyVideoLink() {
      navigator.clipboard.writeText(this.generatedVideoLink).then(() => {
        this.linkCopied = true;
        setTimeout(() => {
          this.linkCopied = false;
        }, 2000);
      });
    },

    openVideoLink() {
      window.open(this.generatedVideoLink, "_blank");
    },

    titleCase,
  },
};
</script>
<style lang="scss" scoped>
.property-list {
  dd {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  &.two-columns {
    grid-template-columns: repeat(2, max-content auto);
  }
}
</style>
